import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import noImage from "../../Assets/Images/settings/no-image.png";
import CommentModal from "./CommentModal";

const commentTypeConfig = {
  wow: { label: "Wow!", icon: "thumbs-up" },
  amazing: { label: "Amazing!", icon: "star" },
  great_job: { label: "Great job!", icon: "trophy" },
  welcome: { label: "Welcome!", icon: "handshake" },
  awesome: { label: "You're awesome!", icon: "smile" },
};

const VideoCommentList = ({ 
  imageCdnUrl = "", 
  commentCount, 
  featuredUser, 
  videoId,
  onCommentChange,
  currentUser = null
}) => {
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [optimisticComments, setOptimisticComments] = useState({});
  const scrollContainerRef = useRef(null);
  const currentUserRef = useRef(currentUser);
  const hasLoadedRef = useRef(false);
  const [commentShowModal, setShowCommentModal] = useState(false);

  // Fetch all comments from API
  const fetchComments = useCallback(async () => {
    if (!videoId) return;

    setIsLoading(true);
    try {
      const res = await axios.get(
        `api/user/audition/video/${videoId}/comment`
      );

      if (res.data.status === 200 && res.data.comments) {
        const newComments = res.data.comments.map((comment) => ({
          id: comment.id,
          user: {
            first_name: comment.user?.first_name || "User",
            last_name: comment.user?.last_name || "",
            image: comment.user?.image,
          },
          comment_type: comment.comment,
          created_at: new Date(comment.created_at),
          isPending: false,
        }));

        setComments(newComments);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setIsLoading(false);
    }
  }, [videoId]);

  // Lazy load: fetch comments only when component appears on screen
  useEffect(() => {
    if (!scrollContainerRef.current || hasLoadedRef.current) return;

    const rootObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && videoId) {
          hasLoadedRef.current = true;
          fetchComments();
          rootObserver.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    rootObserver.observe(scrollContainerRef.current);

    return () => {
      rootObserver.disconnect();
    };
  }, [videoId, fetchComments]);

  // Handle new comment (optimistic update)
  const handleAddComment = useCallback((commentData) => {
    const tempId = `temp-${Date.now()}`;
    
    // Optimistic UI update
    const newComment = {
      id: tempId,
      user: {
        first_name: currentUserRef.current?.first_name || "You",
        last_name: currentUserRef.current?.last_name || "",
        image: currentUserRef.current?.image,
      },
      comment_type: commentData.comment_type,
      created_at: new Date(),
      isPending: true,
    };

    setComments((prev) => [newComment, ...prev]);
    setOptimisticComments((prev) => ({
      ...prev,
      [tempId]: {
        videoId: commentData.videoId,
        comment_type: commentData.comment_type,
        auditionId: commentData.audition_id,
        roundId: commentData.round_id,
      },
    }));

    // API call
    axios
      .post("api/user/audition/video/comment", commentData)
      .then((res) => {
        if (res.data.status === 200) {
          // Comment added successfully
          setComments((prev) =>
            prev.map((comment) =>
              comment.id === tempId ? { ...comment, isPending: false } : comment
            )
          );
          
          setOptimisticComments((prev) => {
            const newState = { ...prev };
            delete newState[tempId];
            return newState;
          });

          onCommentChange?.(true, commentData.comment_type);
        } else if (res.data.status === 422) {
          // Comment was deleted (user clicked same comment again)
          setComments((prev) => prev.filter((c) => c.id !== tempId));
          onCommentChange?.(false, commentData.comment_type);
        }
      })
      .catch((error) => {
        console.error("Error posting comment:", error);
        // Rollback optimistic update on error
        setComments((prev) => prev.filter((c) => c.id !== tempId));
        setOptimisticComments((prev) => {
          const newState = { ...prev };
          delete newState[tempId];
          return newState;
        });
      });
  }, [onCommentChange]);

  // Handle delete comment (optimistic update)
  const handleDeleteComment = useCallback((commentId, commentType) => {
    // Optimistic UI update
    setComments((prev) => prev.filter((c) => c.id !== commentId));

    // Create delete request data
    const commentData = optimisticComments[commentId];
    if (commentData) {
      const deleteData = {
        videoId: commentData.videoId,
        comment_type: commentType,
        audition_id: commentData.auditionId,
        round_id: commentData.roundId,
      };

      // API call to delete
      axios
        .post("api/user/audition/video/comment", deleteData)
        .then((res) => {
          if (res.data.status === 422) {
            // Successfully deleted
            onCommentChange?.(false, commentType);
          } else {
            // Restore comment if delete failed
            const deletedComment = comments.find((c) => c.id === commentId);
            if (deletedComment) {
              setComments((prev) => [deletedComment, ...prev]);
            }
          }
        })
        .catch((error) => {
          console.error("Error deleting comment:", error);
          // Restore comment on error
          const deletedComment = comments.find((c) => c.id === commentId);
          if (deletedComment) {
            setComments((prev) => [deletedComment, ...prev]);
          }
        });
    }
  }, [optimisticComments, onCommentChange, comments]);

  // Expose methods to parent component
  useEffect(() => {
    window.__videoCommentListMethods = {
      addComment: handleAddComment,
      deleteComment: handleDeleteComment,
    };

    return () => {
      window.__videoCommentListMethods = null;
    };
  }, [handleAddComment, handleDeleteComment]);

  const resolveAvatar = (comment) => {
    if (comment?.user?.image && comment.user.image !== "") {
      return `${imageCdnUrl}${comment.user.image}`;
    }
    return noImage;
  };

  const getCommentText = (comment) => {
    const config = commentTypeConfig[comment.comment_type];
    return config ? config.label : comment.comment_type;
  };

  const formatTime = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "moments ago";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  const totalLabel =
    typeof commentCount === "number" 
      ? `${commentCount + Object.keys(optimisticComments).length} total` 
      : `${comments.length} comments`;

  const handleCommentModalOpen = () => {
    setShowCommentModal(true);
  };

  return (
    <>
    <div className="video-comment-wrapper">
      <div className="video-comment-header">
        <div className="d-flex align-items-center gap-2">
          <span className="video-comment-pip"></span>
          <h6 className="mb-0 text-warning">Comments</h6>
          {/* <small className="text-secondary">{totalLabel}</small> */}
        </div>
      </div>

      <div className="video-comment-scroll" ref={scrollContainerRef}>
        {comments.length === 0 && !isLoading ? (
          <div className="text-center text-secondary py-4">
            <small>No comments yet. Be the first to comment!</small>
          </div>
        ) : (
          <>
            {comments.map((comment) => (
              <div 
                className={`video-comment-item ${comment.isPending ? 'opacity-75' : ''}`}
                key={comment.id}
              >
                <img
                  className="video-comment-avatar"
                  src={resolveAvatar(comment)}
                  alt={`${comment.user?.first_name} avatar`}
                  style={{ width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0 }}
                  onError={(event) => {
                    event.target.onerror = null;
                    event.target.src = noImage;
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                  <span className="text-light fw-semibold" style={{ fontSize: '14px' }}>
                    {comment.user?.first_name} {comment.user?.last_name}
                  </span>
                  <span className="text-secondary" style={{ fontSize: '14px' }}>
                    commented
                  </span>
                  <span style={{ fontWeight: '800', fontSize: '14px', color: '#ffc107' }}>
                    {getCommentText(comment)}
                  </span>
                  {comment.isPending && (
                    <span className="text-warning" style={{ fontSize: '11px' }}>
                      (submitting...)
                    </span>
                  )}
                  <span className="text-secondary" style={{ fontSize: '12px', marginLeft: 'auto' }}>
                    {formatTime(comment.created_at)}
                  </span>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="d-flex justify-content-center align-items-center py-3">
                <div className="spinner-border spinner-border-sm text-warning" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

            {!isLoading && comments.length > 0 && (
              <div className="text-center text-secondary py-3">
                <small>All comments loaded</small>
              </div>
            )}
          </>
        )}
      </div>
      <div className="video-comment-footer" onClick={handleCommentModalOpen}>
        <div className="d-flex align-items-center gap-2">
          <i className="fa-solid fa-comment" style={{ fontSize: '30px', color: '#ffc107' }}></i>
          <h6 className="mb-0 text-warning">New comment</h6>
        </div>
      </div>
    </div>
    <CommentModal
        show={commentShowModal}
        onHide={() => setShowCommentModal(false)}
        commentModalData={[videoId, null, null]}
        getVideFeedComment={() => fetchComments()}
      />
    </>
  );
};

export default VideoCommentList;